/**
 * The single-page-app half of the funnel.
 *
 * The edge sees one document request per app — the initial document. Every
 * route the visitor reaches after that is a client-side navigation the server
 * never hears about, so the browser has to say so.
 *
 * It says so to OUR OWN origin, carrying no key and reaching no third party:
 * the edge endpoint derives everything that matters (identity, consent,
 * geography) from the request it receives and does the sending server-side.
 * The ingestion key therefore never exists in client JavaScript, which is the
 * invariant this split is here to protect.
 */
/** Same-origin endpoint every app exposes for client-side navigations. */
export const CLIENT_PAGEVIEW_ROUTE = '/api/pv';
/** Bounds an untrusted path: URLs get long, and a path never legitimately does. */
const MAX_PATH_LENGTH = 2048;
/**
 * Control characters are header-injection and log-forging material once a path
 * is resolved into a URL; no real path contains one.
 */
const CONTROL_CHARACTERS = /[\u0000-\u001f\u007f]/;
/**
 * A path that resolves against our own origin and nowhere else.
 *
 * `//evil.com` and `/\evil.com` are protocol-relative: `new URL()` reads them
 * as another host entirely, which would turn the endpoint into a relay for
 * pageviews on sites we do not own.
 */
function isSameOriginPath(value) {
    return (typeof value === 'string' &&
        value.length > 0 &&
        value.length <= MAX_PATH_LENGTH &&
        value.startsWith('/') &&
        !value.startsWith('//') &&
        !value.startsWith('/\\') &&
        !CONTROL_CHARACTERS.test(value));
}
/** Parses an untrusted request body into the shape above, or null if it is not one. */
export function readClientPageviewBody(raw) {
    if (typeof raw !== 'object' || raw === null)
        return null;
    const { path, referrerPath } = raw;
    if (!isSameOriginPath(path))
        return null;
    return {
        path,
        referrerPath: isSameOriginPath(referrerPath) ? referrerPath : null,
    };
}
/**
 * Post a client-side navigation to this app's own endpoint. `keepalive` lets
 * the request outlive a route change that unmounts the page; failures are
 * swallowed, because a pageview that did not record is never a reason for the
 * app to misbehave.
 */
export function sendClientPageview(body) {
    try {
        void fetch(CLIENT_PAGEVIEW_ROUTE, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
            keepalive: true,
            credentials: 'same-origin',
        }).catch(() => { });
    }
    catch {
        // Never break navigation for analytics.
    }
}
/**
 * Report a completed route change, if it is one worth reporting. Shaped to drop
 * straight into `router.afterEach`, so an app registers it and adds nothing.
 *
 * Two rules, and they are the whole of it:
 *
 * 1. A pageview is a change of PATH, not of query state. Apps rewrite the query
 *    in place constantly — stripping a one-shot trigger param on mount, keeping
 *    a search box shareable as the visitor types, deep-linking a card on click —
 *    and none of that is a page anyone navigated to. Counting it turns one
 *    visit into a pageview per keystroke. The inbound query that carries real
 *    attribution (`?utm_source=`, `?gclid=`) rides the first document instead,
 *    which the edge records with the query intact.
 *
 * 2. The page the browser asked for was already recorded by the edge, so
 *    the navigation that boots the app is not reported again. Unless it was
 *    REDIRECTED: then the server recorded where the visitor was sent FROM, and
 *    the destination they actually landed on — `/` → `/dashboard`, `/hub` →
 *    `/hub/affiliate` — is a page nothing else will ever record.
 *
 * A navigation that failed never rendered, so it is not a pageview either.
 */
export function trackRouteChange(to, from, failure) {
    if (failure)
        return;
    // What the visitor was looking at before this navigation resolved. On the
    // boot navigation there is no such route — `from` is the router's start
    // location — so the redirect origin stands in for it, and its absence means
    // the server already recorded exactly this page.
    const previous = from.matched.length === 0 ? to.redirectedFrom : from;
    if (!previous)
        return;
    if (to.path === previous.path)
        return;
    sendClientPageview({ path: to.fullPath, referrerPath: previous.fullPath });
}
